import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label} from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import OtherTextInput from '../Components/layout/OtherTextInput';
import { CONTROL_ACCESSORIES } from './ConstAccessMethodsMock';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import { OTHER_CKBX_VAL, isOtherChecked } from './OtherCheckboxUtils';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_SETUP_ACCESSORIES_HELP = `
  <div>
    <p>Accessories are things that are intended to help the client use the access method.  
    Sometimes these are called “control enhancers,” as they may help accuracy as well as efficiency. 
    They may provide other benefits such as reduced fatigue, or less pain. 
    </p>
    <p>
    We’ve listed a variety of accessories here, and you can learn more about them by choosing the little help icon next to each one.  
    If there’s something we haven’t listed here, just enter it in the Other option.     
    </p>
    <p>Finally, if you want to note the specific details of the accessory, such as the specific stylus used, you can enter that in the Other Setup Notes on the next page. You could also check Other here, as well as Stylus, and enter the specific stylus in the Other textbox. </p>
  </div>
`;

const propTypes = {
  activeAccessMethodName: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupName: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  onChangeActiveSetupAccessories: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  activeAccessMethodName: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupAccessories: [],
  activeSetupName: '',
};

//content sub-component with unique content for this page
class TestDriveSetupAccessoriesContent extends Component {

  //Render the form for asking about setup info.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the vars in the parent, 
  //which triggers a new render of the view with the new var values.
  render(){
    //these can be changed:
    const activeSetupAccessories = this.props.activeSetupAccessories;
    const onChangeActiveSetupAccessories = this.props.onChangeActiveSetupAccessories;
    const otherAccessory = this.props.otherAccessory;
    const onChangeOtherAccessory = this.props.onChangeOtherAccessory

    const availableBtns = CONTROL_ACCESSORIES;
    let otherChecked = isOtherChecked(activeSetupAccessories);

    return(
    <>
    <p><strong>Note: </strong>Not all of these will apply to your situation. But take a look just in case. If you're not using any, just Continue.</p>

    <Form>
      <FormGroup tag="fieldset">
        <legend>Choose all accessories you'll be using:</legend>
        {availableBtns.map((btn, index) =>
          <React.Fragment key={index}>
          <FormGroup check>
            <Input
              id={btn.id}
              value={btn.id}
              checked={(activeSetupAccessories.findIndex( (plat) => plat.startsWith(btn.id)) > -1)}
              onChange={onChangeActiveSetupAccessories}
              name="accessories"
              type="checkbox"
            />
            {' '}
            <Label for={btn.id} check>
              {btn.label}
              {' '}
              <span className="aa-more-info">{btn.moreInfo}</span>
            </Label>
            {' '}
            {btn.help ?
              <LearnMoreModal linkText="" buttonClassName="aa-no-padding aa-secondary-link" title={btn.label} content={btn.help || 'No help found'}/>
              : null
            }
          </FormGroup>
          {/* If the btn is 'other', include a text field to specify */}
          { ((btn.value === OTHER_CKBX_VAL)) 
            ?
              <OtherTextInput
                otherChecked={otherChecked}
                otherText={otherAccessory}
                onChangeOtherText={onChangeOtherAccessory}
              />
            : null
          }
          </React.Fragment>
        )}
      </FormGroup>
    </Form>
    </>
  );
}
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveSetupAccessories(props) {
  //props for setup info section
  const activeAccessMethodName = props.activeAccessMethodName;
  const activeSetupSelectionMethod = props.activeSetupSelectionMethod;
  const activeSetupBodySite = props.activeSetupBodySite;
  const activeSetupName = props.activeSetupName;
  const activeSetupAccessories = props.activeSetupAccessories;
  const onChangeActiveSetupAccessories = props.onChangeActiveSetupAccessories;
  const otherAccessory = props.otherAccessory;
  const onChangeOtherAccessory = props.onChangeOtherAccessory
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;

  const dev = props.dev || {};

  console.log("rendering TestDriveSetupAccessories, with props: " + JSON.stringify(props));

  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Do you want to use any accessories in this Test-drive?`,
    subtitle: '',
    // subTitle: "These are optional items that can sometimes enhance control and performance.",
    learnMore: {linkText: "Learn More about accessories",
                title: "Accessory options",
                content: TD_SETUP_ACCESSORIES_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('TestDriveSetupAccessories mounted');
    return () => {
      console.log('TestDriveSetupAccessories unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveSetupAccessoriesContent 
          activeSetupAccessories={activeSetupAccessories}
          onChangeActiveSetupAccessories={onChangeActiveSetupAccessories}
          otherAccessory={otherAccessory}
          onChangeOtherAccessory={onChangeOtherAccessory}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        :
  
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
       />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupAccessories.propTypes = propTypes;
TestDriveSetupAccessories.defaultProps = defaultProps;

export default TestDriveSetupAccessories;